// CANONICAL SOURCE for all palette and typography values in Front Line.
// No hardcoded hex anywhere else in the codebase — import from here instead.
//
// Two-theme system: DARK_TOKENS and LIGHT_TOKENS export the v2 palette per
// theme. The default export re-exports DARK_TOKENS so existing imports keep
// working.
//
// IMPORTANT: these values are kept manually in sync with the @theme block in
// app/globals.css. There is no build-time generation step yet — when editing
// one, edit the other.

export const DARK_TOKENS = {
  pageBg: "#100d0b",      // page-level (behind the card surface)
  bg: "#1a1614",          // warm charcoal base, not pure black
  bg2: "#221d19",
  bg3: "#2a2520",
  rule: "#2e2823",
  rule2: "#3a322c",
  ink: "#EDE8E0",
  dim: "#9a948a",
  faint: "#5a554d",
  clay: "#D85F2E",
  clayDeep: "#C4572E",
  olive: "#6A7D4A",
  oliveBright: "#8ea968",
  onClay: "#0B0B0D",      // text on clay buttons
  cardShadow: "#00000000",   // no elevated shadow in dark mode
} as const;

export const LIGHT_TOKENS = {
  pageBg: "#E8E2D2",      // page-level (behind the card surface)
  bg: "#F5F1E8",          // sand
  bg2: "#FAF7F0",         // card paper
  bg3: "#FFFDF7",         // elevated paper
  rule: "#E0D8C5",
  rule2: "#CFC4AE",
  ink: "#1A1714",         // same warm charcoal as dark base, role inverted
  dim: "#6B6358",
  faint: "#9A9387",
  clay: "#C4572E",        // deeper than dark — bright clay washes on cream
  clayDeep: "#A8472A",
  olive: "#5d6d3f",       // deeper than dark for the same reason
  oliveBright: "#7a8c52",
  onClay: "#FAF7F0",
  cardShadow: "rgba(26, 23, 20, 0.06)",
} as const;

/**
 * Stand-in for GT America Extended Medium until license is acquired.
 * Single point of change for future swap.
 */
export const FONT_WORDMARK = "Inter";

// Per-theme typography spec. Font-family strings reference the next/font
// CSS variables set on <html> in app/layout.tsx; a font swap there
// propagates through these exports automatically.
export interface ThemeFonts {
  readonly sans: string;
  readonly mono: string;
  readonly display: string;
  readonly displayWeight: number;
  readonly italic: boolean;
}

export const DARK_FONTS: ThemeFonts = {
  sans: "var(--font-body), system-ui, sans-serif",
  mono: "var(--font-mono-dark), ui-monospace, monospace",
  display: "var(--font-body), system-ui, sans-serif",
  displayWeight: 600,
  italic: false,
};

export const LIGHT_FONTS: ThemeFonts = {
  // --font-data is JetBrains Mono per next/font; doubles as the light-theme
  // mono. Display is Fraunces (italic by spec); sans is Inter Tight.
  sans: "var(--font-sans-light), system-ui, sans-serif",
  mono: "var(--font-data), ui-monospace, monospace",
  display: "var(--font-display-light), Georgia, serif",
  displayWeight: 500,
  italic: true,
};

export default DARK_TOKENS;
